import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Settings, Loader2, Save, Plus, Trash2, MailCheck, CheckCircle2, XCircle, Pencil, Mail, BadgeEuro, ReceiptText, CreditCard, HardDrive, Cloud, AlertTriangle, ChevronDown, ChevronRight, ArrowDownAZ, RefreshCw, FileText, Image as ImageIcon } from "lucide-react";
import { invalidatePrintMarginsCache } from "../../components/RichTextEditor";
import AbbonamentoCard from "../../components/AbbonamentoCard";
import { IntestazioneEditor } from "../../components/IntestazioneEditor";
import { AreaClienteHeaderSettings } from "../../components/AreaClienteHeaderSettings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { ValidationHint } from "../../components/ValidationHint";
import { validateIBAN } from "../../lib/validators";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useTheme } from "../../context/ThemeContext";
import { REQUIREABLE_FIELDS, REQ_GROUPS, DEFAULT_REQUIRED } from "../../lib/requiredFields";
import { DEFAULT_STATI } from "../../lib/statiAggiuntivi";
import { normTipologie, clearTipologieCache } from "../../lib/tipologieSinistro";
import { COPERTINA_CATALOG, DEFAULT_COPERTINA_FIELDS } from "../../lib/copertina";
import CopertinaEditor from "../../components/CopertinaEditor";
import WaTemplatesSettings from "../../components/WaTemplatesSettings";
import FrasarioDiarioSettings from "../../components/FrasarioDiarioSettings";
import { useAuth } from "../../context/AuthContext";

function SecurityEmailCard() {
  const { user, refresh } = useAuth();
  const [otp, setOtp] = useState(user?.otp_email || "");
  const [saving, setSaving] = useState(false);
  const saveOtp = async () => {
    setSaving(true);
    try {
      await api.patch("/me/otp-email", { otp_email: otp.trim() });
      if (refresh) await refresh();
      toast.success("Email di sicurezza aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div id="sez-sicurezza" className="pgp-card p-6 max-w-2xl pgp-fade-up space-y-4 mb-6" data-testid="security-email-config">
      <div className="flex items-center gap-2 text-slate-100 font-semibold"><MailCheck className="h-5 w-5 text-amber-400" /> Email notifiche di sicurezza</div>
      <p className="text-sm text-slate-400">Indirizzo a cui inviare i codici OTP e le notifiche di sicurezza del tuo account. Se vuoto viene usata l'email di login.</p>
      <div className="flex gap-2">
        <Input type="email" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="email@studio.it" data-testid="security-email-input" className="bg-slate-900/60 border-slate-700" />
        <Button onClick={saveOtp} disabled={saving} data-testid="security-email-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
        </Button>
      </div>
    </div>
  );
}

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};

const FIELDS = [
  { key: "studio_name", label: "Nome studio", top: true },
  { key: "codice_studio", label: "Codice Studio (assegnato automaticamente)", mono: true, readOnly: true },
  { key: "indirizzo", label: "Indirizzo (via e n° civico)" },
  { key: "cap", label: "CAP" },
  { key: "citta", label: "Comune (Provincia)" },
  { key: "provincia", label: "Provincia" },
  { key: "telefono", label: "Telefono" },
  { key: "cellulare", label: "Cellulare" },
  { key: "fax", label: "Fax" },
  { key: "email_contatto", label: "Email di contatto" },
  { key: "pec", label: "PEC", mono: true },
  { key: "sito_web", label: "Sito web" },
  { key: "partita_iva", label: "Partita IVA", mono: true },
  { key: "codice_fiscale", label: "Codice fiscale studio", mono: true },
  { key: "albo", label: "Numero iscrizione albo" },
  { key: "iban", label: "IBAN liquidazioni", mono: true },
];
const SETTINGS_TABS = [
  { key: "dati", label: "Dati Studio", color: "#3b82f6", anchor: "sez-dati-studio", secs: ["sez-dati-studio"], part: "dati" },
  { key: "titolare", label: "Titolare e Sicurezza", color: "#f43f5e", anchor: "sez-sicurezza", secs: ["sez-sicurezza"] },
  { key: "intestazione", label: "Intestazione", color: "#f59e0b", anchor: "sez-intestazione", secs: ["sez-dati-studio"], part: "intestazione" },
  { key: "firma", label: "Firma Email", color: "#8b5cf6", anchor: "sez-firma", secs: ["sez-dati-studio"], part: "firma" },
  { key: "associati", label: "Studi Associati", color: "#14b8a6", anchor: "sez-studi-associati", secs: ["sez-studi-associati"] },
  { key: "conti", label: "Conti Correnti", color: "#10b981", anchor: "sez-conti-correnti", secs: ["sez-conti-correnti"] },
  { key: "email", label: "Email", color: "#0ea5e9", anchor: "sez-email", secs: ["sez-email"] },
  { key: "pec", label: "PEC", color: "#6366f1", anchor: "sez-pec", secs: ["sez-pec"] },
  { key: "whatsapp", label: "WhatsApp", color: "#22c55e", anchor: "sez-whatsapp", secs: ["sez-whatsapp"] },
  { key: "area-cliente", label: "Area Cliente", color: "#ec4899", anchor: "sez-area-cliente", secs: ["sez-area-cliente"] },
  { key: "frasario", label: "Frasario", color: "#84cc16", anchor: "sez-frasario", secs: ["sez-frasario"] },
  { key: "pratica", label: "Pratica", color: "#f97316", anchor: "sez-dinamiche", secs: ["sez-dinamiche", "sez-campi-obbligatori", "sez-stati-aggiuntivi", "sez-ruoli-sinistro", "sez-tipologie-sinistro", "sez-campi-copertina"] },
  { key: "fatturazione", label: "Fatturazione e Tariffe", color: "#eab308", anchor: "sez-fatturazione", secs: ["sez-tariffe", "sez-fatturazione", "sez-impostazioni-fattura", "sez-voci-proforma"] },
  { key: "storage", label: "Storage", color: "#06b6d4", anchor: "sez-storage", secs: ["sez-storage", "sez-storage-usage", "sez-thumbs"] },
  { key: "stampe", label: "Stampe e Preferenze", color: "#d946ef", anchor: "sez-impostazioni-stampe", secs: ["sez-impostazioni-stampe", "sez-preferenze"] },
  { key: "abbonamento", label: "Abbonamento", color: "#ef4444", anchor: "sez-abbonamento", secs: ["sez-abbonamento"] },
];
const findTab = (id) => SETTINGS_TABS.find((t) => t.anchor === id) || SETTINGS_TABS.find((t) => t.secs.includes(id)) || SETTINGS_TABS[0];

function SettingsTabs({ active, onSelect, hidden = [] }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6 max-w-5xl" data-testid="settings-tabs">
      {SETTINGS_TABS.filter((t) => !hidden.includes(t.key)).map((t) => {
        const on = t.key === active.key;
        return (
          <button key={t.key} type="button" onClick={() => onSelect(t)} data-testid={`settings-tab-${t.key}`}
            style={{ borderColor: on ? t.color : `${t.color}55`, background: on ? `${t.color}26` : "transparent", color: on ? "#f8fafc" : undefined }}
            className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800/60">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: t.color }} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

const TIPI_SINISTRO = ["RD", "RCA", "TRASPORTATO", "PEDONE", "CICLISTA", "ANIMALE", "ANOMALIA STRADA", "ALTRO"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_KEYS = ["email_contatto", "pec"];
const isEmailInvalid = (key, val) => EMAIL_KEYS.includes(key) && !!(val || "").trim() && !EMAIL_RE.test((val || "").trim());

const ASSOC_FIELDS = [
  { key: "nome", label: "Nome studio", full: true },
  { key: "indirizzo", label: "Indirizzo", full: true },
  { key: "citta", label: "CAP e comune" },
  { key: "partita_iva", label: "Partita IVA" },
  { key: "codice_fiscale", label: "Codice fiscale" },
  { key: "telefono", label: "Telefono" },
  { key: "cellulare", label: "Cellulare" },
  { key: "email", label: "Email" },
  { key: "pec", label: "PEC" },
];

const CONTO_FIELDS = [
  { key: "nome", label: "Nome conto (es. Conto principale)", full: true },
  { key: "intestatario", label: "Intestatario" },
  { key: "banca", label: "Banca" },
  { key: "iban", label: "IBAN", mono: true, full: true },
  { key: "bic_swift", label: "BIC/SWIFT", mono: true },
  { key: "filiale", label: "Filiale (opzionale)" },
  { key: "note", label: "Note (opzionale)", full: true },
];

const COP_SIZE_OPTS = [8, 10, 12, 14, 16];
const COP_HIGHLIGHT_OPTS = [
  { v: "", label: "Nessuno" },
  { v: "#fff59d", label: "Giallo" },
  { v: "#c8e6c9", label: "Verde" },
  { v: "#bbdefb", label: "Azzurro" },
  { v: "#f8bbd0", label: "Rosa" },
  { v: "#ffe0b2", label: "Arancio" },
  { v: "#e0e0e0", label: "Grigio" },
];

const FIRMA_FIELDS = [
  { key: "nome", label: "Nome Studio" },
  { key: "indirizzo", label: "Indirizzo completo (via, CAP, comune, provincia)" },
  { key: "telefono", label: "Telefono" },
  { key: "cellulare", label: "Cellulare" },
  { key: "fax", label: "Fax" },
  { key: "email", label: "Studio di posta elettronica" },
  { key: "pec", label: "PEC" },
  { key: "sito_web", label: "Sito web" },
  { key: "partita_iva", label: "Partita IVA" },
  { key: "codice_fiscale", label: "Codice fiscale studio" },
  { key: "albo", label: "Numero iscrizione albo" },
];
const firmaVals = (f) => {
  const s = f || {};
  const name = (s.studio_name || s.intestazione || "").trim().split("\n")[0].trim();
  const prov = (s.provincia || "").trim();
  const citta0 = (s.citta || "").trim();
  const cittaProv = prov && citta0 && !citta0.includes("(") ? `${citta0} (${prov})` : citta0;
  const city = [s.cap, cittaProv].map((x) => (x || "").trim()).filter(Boolean).join(" ");
  const addr = [s.indirizzo, city].map((x) => (x || "").trim()).filter(Boolean).join(" - ");
  return {
    nome: name,
    indirizzo: addr,
    telefono: s.telefono ? `Tel. ${s.telefono}` : "",
    cellulare: s.cellulare ? `Cell. ${s.cellulare}` : "",
    fax: s.fax ? `Fax ${s.fax}` : "",
    email: (s.email_contatto || "").trim(),
    pec: s.pec ? `PEC: ${s.pec}` : "",
    sito_web: (s.sito_web || "").trim(),
    partita_iva: s.partita_iva ? `P.IVA ${s.partita_iva}` : "",
    codice_fiscale: s.codice_fiscale ? `C.F. ${s.codice_fiscale}` : "",
    albo: s.albo ? `Iscr. Albo ${s.albo}` : "",
  };
};
const firmaHasData = (key, f) => !!firmaVals(f)[key];
const composeFirma = (f) => {
  const custom = (f.firma_email || "").trim();
  if (custom) return custom;
  const sel = f.firma_fields || [];
  const vals = firmaVals(f);
  return FIRMA_FIELDS.filter((x) => sel.includes(x.key)).map((x) => vals[x.key]).filter(Boolean).join("\n");
};

const DEFAULT_RUOLI_SINISTRO = ["Danneggiato", "Responsabile", "Corresponsabile", "Terzo Trasportato", "Proprietario", "Assicurato"];

export default function Impostazioni() {
  const { user: authUser } = useAuth();
  const isDemo = !!authUser?.demo_registered;
  const demoMsg = <p className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="demo-mail-disabled-msg">Funzione non disponibile nella versione demo. Attiva il tuo studio per configurare email e PEC.</p>;
  const { accent, setAccent, ACCENTS } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [proformaVoci, setProformaVoci] = useState([]);
  const [diciture, setDiciture] = useState([]);
  const [conti, setConti] = useState([]);
  const [ibanBlurred, setIbanBlurred] = useState({});
  const [savingConti, setSavingConti] = useState(false);
  const [copTplOpen, setCopTplOpen] = useState(false);
  const [copModels, setCopModels] = useState([]);
  const [copModelId, setCopModelId] = useState("__default__");
  const [copTemplate, setCopTemplate] = useState("");
  const [copDefault, setCopDefault] = useState({ fields: null, styles: {}, template: "" });
  const [dinList, setDinList] = useState([]);
  const [dinEdit, setDinEdit] = useState(null); // { id, testo, tipi }
  const [savingDin, setSavingDin] = useState(false);
  const [pec, setPec] = useState(null);
  const [pecProviders, setPecProviders] = useState({});
  const [pecPw, setPecPw] = useState("");
  const [pecSaving, setPecSaving] = useState(false);
  const [pecTesting, setPecTesting] = useState(false);
  const [pecTest, setPecTest] = useState(null);
  const [storage, setStorage] = useState(null);
  const [usage, setUsage] = useState(null);
  const [storageOk, setStorageOk] = useState(false); // sezione Storage solo per il primo studio creato
  const [openStorage, setOpenStorage] = useState(false);
  const [usageRefreshing, setUsageRefreshing] = useState(false);
  const [openPerPratica, setOpenPerPratica] = useState(false);
  const [gcsJson, setGcsJson] = useState("");
  const [wasabiAccessKey, setWasabiAccessKey] = useState("");
  const [wasabiSecretKey, setWasabiSecretKey] = useState("");
  const [storageSaving, setStorageSaving] = useState(false);
  const [storageTesting, setStorageTesting] = useState(false);
  const [storageTest, setStorageTest] = useState(null);
  const [thumbStatus, setThumbStatus] = useState(null);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [email, setEmail] = useState(null);
  const [emailProviders, setEmailProviders] = useState({});
  const [emailPw, setEmailPw] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTest, setEmailTest] = useState(null);
  const [reqFields, setReqFields] = useState([]);
  const [reqSaving, setReqSaving] = useState(false);
  const [stati, setStati] = useState([]);
  const [statiSaving, setStatiSaving] = useState(false);
  const [ruoli, setRuoli] = useState([]);
  const [ruoliSaving, setRuoliSaving] = useState(false);
  const [statiUpper, setStatiUpper] = useState(false);
  const [ruoliUpper, setRuoliUpper] = useState(false);
  const [tipologie, setTipologie] = useState([]);
  const [tipSaving, setTipSaving] = useState(false);
  const [dragTip, setDragTip] = useState(null);
  const [copFields, setCopFields] = useState([]);
  const [copStyles, setCopStyles] = useState({});
  const [copSaving, setCopSaving] = useState(false);
  const [dragCop, setDragCop] = useState(null);
  const [dragStato, setDragStato] = useState(null);
  const [dragRuolo, setDragRuolo] = useState(null);

  useEffect(() => {
    if (loading) return;
    const id = (location.hash || "").replace("#", "");
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash, location.key, loading]);

  useEffect(() => {
    Promise.all([
      api.get("/studio/me"),
      api.get("/studio/dinamiche-list").catch(() => ({ data: [] })),
      api.get("/studio/pec/config").catch(() => ({ data: { configured: false } })),
      api.get("/studio/email/config").catch(() => ({ data: { configured: false } })),
      api.get("/studio/storage/usage").catch(() => ({ data: null })),
      api.get("/studio/storage/access").catch(() => ({ data: { allowed: false } })),
    ]).then(([me, d, pc, em, us, stAcc]) => {
      setStorageOk(!!stAcc.data?.allowed);
      const merged = { studio_name: me.data.studio_name, ...(me.data.settings || {}) };
      const defFields = FIRMA_FIELDS.filter((x) => firmaHasData(x.key, merged)).map((x) => x.key);
      setForm({
        ...merged,
        firma_fields: Array.isArray(merged.firma_fields) ? merged.firma_fields : defFields,
        firma_enabled: merged.firma_enabled !== false,
      });
      setConti(Array.isArray(merged.conti_correnti) ? merged.conti_correnti : []);
      setProformaVoci(Array.isArray(me.data.settings?.proforma_voci) ? me.data.settings.proforma_voci : []);
      const _dl0 = me.data.settings?.proforma_diciture;
      if (Array.isArray(_dl0)) {
        setDiciture(_dl0);
      } else {
        const _seed = [{ id: `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, nome: "Forfettario (standard)", testo: "Operazione effettuata ai sensi dell'art. 1, commi 54-89, L. 190/2014 - Regime Forfettario", regime: "forfettario", principale: true }];
        setDiciture(_seed);
        api.patch("/studio/settings", { proforma_diciture: _seed }).catch(() => {});
      }
      setFatt({ regime_default: "forfettario", iva_pct: "", ritenuta_pct: "", cassa_pct: "", codice_sdi: "", mostra_filiale: false, ...(me.data.settings?.fatturazione_defaults || {}) });
      const rf = me.data.settings?.required_fields;
      setReqFields(rf !== undefined && rf !== null ? rf : DEFAULT_REQUIRED);
      const sa = me.data.settings?.stato_aggiuntivo_options;
      setStati(Array.isArray(sa) && sa.length ? sa : DEFAULT_STATI);
      const rl = me.data.settings?.ruolo_sinistro_options;
      setRuoli(Array.isArray(rl) && rl.length ? rl : DEFAULT_RUOLI_SINISTRO);
      setTipologie(normTipologie(me.data.settings?.tipologie_sinistro));
      const cf = me.data.settings?.copertina_fields;
      const enabledKeys = Array.isArray(cf) && cf.length ? cf : DEFAULT_COPERTINA_FIELDS;
      const defStyles = me.data.settings?.copertina_field_styles && typeof me.data.settings.copertina_field_styles === "object" ? me.data.settings.copertina_field_styles : {};
      const defTpl = me.data.settings?.copertina_template || "";
      setCopDefault({ fields: enabledKeys, styles: defStyles, template: defTpl });
      setCopModels(Array.isArray(me.data.settings?.copertina_models) ? me.data.settings.copertina_models : []);
      setCopModelId("__default__");
      setCopTemplate(defTpl);
      setCopFields([
        ...enabledKeys.map((k) => COPERTINA_CATALOG.find((c) => c.key === k)).filter(Boolean).map((c) => ({ ...c, enabled: true })),
        ...COPERTINA_CATALOG.filter((c) => !enabledKeys.includes(c.key)).map((c) => ({ ...c, enabled: false })),
      ]);
      setCopStyles(defStyles);
      setDinList(Array.isArray(d.data) ? d.data : []);
      const pcd = pc.data || {};
      setPecProviders(pcd.providers || {});
      setPec({
        mailbox: pcd.mailbox || "", username: pcd.username || "", provider: pcd.provider || "aruba",
        smtp_host: pcd.smtp_host || "smtps.pec.aruba.it", smtp_port: pcd.smtp_port || 465, smtp_security: pcd.smtp_security || "ssl",
        imap_host: pcd.imap_host || "imaps.pec.aruba.it", imap_port: pcd.imap_port || 993, imap_security: pcd.imap_security || "ssl",
        enabled: pcd.enabled ?? true, has_password: !!pcd.has_password, configured: !!pcd.configured,
      });
      setStorage(null);
      setUsage(us.data || null);
      const emd = em.data || {};
      setEmailProviders(emd.providers || {});
      setEmail({
        from_email: emd.from_email || "", from_name: emd.from_name || "", username: emd.username || "", provider: emd.provider || "altro",
        smtp_host: emd.smtp_host || "", smtp_port: emd.smtp_port || 465, smtp_security: emd.smtp_security || "ssl",
        imap_host: emd.imap_host || "", imap_port: emd.imap_port || 993, imap_security: emd.imap_security || "ssl",
        enabled: emd.enabled ?? true, has_password: !!emd.has_password, configured: !!emd.configured,
      });
      setLoading(false);
      if (stAcc.data?.allowed) loadThumbStatus();
    }).catch(() => setLoading(false));
  }, []);

  const setPecField = (k, v) => setPec((p) => ({ ...p, [k]: v }));
  const onProvider = (prov) => {
    const info = pecProviders[prov];
    setPec((p) => ({ ...p, provider: prov, ...(info && prov !== "altro" ? { smtp_host: info.smtp_host, smtp_port: info.smtp_port, imap_host: info.imap_host, imap_port: info.imap_port } : {}) }));
  };
  const savePec = async () => {
    if (!pec.mailbox) { toast.error("Inserisci l'indirizzo della casella PEC"); return; }
    if (!pec.has_password && !pecPw) { toast.error("Inserisci la password della casella PEC"); return; }
    setPecSaving(true);
    try {
      const payload = {
        mailbox: pec.mailbox, username: pec.username || pec.mailbox, provider: pec.provider,
        smtp_host: pec.smtp_host, smtp_port: Number(pec.smtp_port), smtp_security: pec.smtp_security,
        imap_host: pec.imap_host, imap_port: Number(pec.imap_port), imap_security: pec.imap_security, enabled: pec.enabled,
      };
      if (pecPw) payload.password = pecPw;
      const { data } = await api.put("/studio/pec/config", payload);
      setPec((p) => ({ ...p, ...data }));
      setPecPw("");
      toast.success("Configurazione PEC salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPecSaving(false); }
  };
  const testPec = async () => {
    setPecTesting(true); setPecTest(null);
    try {
      const { data } = await api.post("/studio/pec/config/test");
      setPecTest(data);
      if (data.ok) toast.success("Connessione PEC riuscita (SMTP + IMAP)");
      else toast.error("Connessione PEC fallita — controlla i dati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPecTesting(false); }
  };

  const changeStorageProvider = (p) => {
    setStorage((s) => ({ ...s, provider: p }));
    if (p !== "emergent") toast.info("I file già caricati resteranno sullo storage precedente. Solo i nuovi upload useranno il nuovo storage.");
  };
  const saveStorage = async () => {
    setStorageSaving(true);
    try {
      const payload = { provider: storage.provider, project_id: storage.project_id, bucket_name: storage.bucket_name };
      if (gcsJson.trim()) payload.service_account_json = gcsJson.trim();
      payload.wasabi_endpoint = storage.wasabi_endpoint || "";
      payload.wasabi_region = storage.wasabi_region || "";
      if (wasabiAccessKey) payload.wasabi_access_key = wasabiAccessKey;
      if (wasabiSecretKey) payload.wasabi_secret_key = wasabiSecretKey;
      const { data } = await api.put("/studio/storage/config", payload);
      setStorage((s) => ({ ...s, ...data })); setGcsJson(""); setWasabiAccessKey(""); setWasabiSecretKey("");
      toast.success("Configurazione storage salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStorageSaving(false); }
  };
  const testStorage = async () => {
    setStorageTesting(true); setStorageTest(null);
    try {
      const { data } = await api.post("/studio/storage/test");
      setStorageTest(data);
      if (data.ok) toast.success(`Connessione a ${storage.provider === "wasabi" ? "Wasabi" : "Google Cloud Storage"} riuscita`);
      else toast.error(`Connessione fallita: ${data.error || ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStorageTesting(false); }
  };
  const refreshUsage = async () => {
    setUsageRefreshing(true);
    try {
      const { data } = await api.get("/studio/storage/usage");
      setUsage(data);
      toast.success("Spazio ricalcolato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUsageRefreshing(false); }
  };
  const loadThumbStatus = async () => {    try { const { data } = await api.get("/studio/allegati/thumbs/status"); setThumbStatus(data); return data; }
    catch { return null; }
  };
  const rebuildThumbs = async () => {
    setThumbBusy(true);
    try {
      await api.post("/studio/allegati/thumbs/rebuild");
      toast.success("Rigenerazione miniature avviata");
      const poll = async () => {
        const d = await loadThumbStatus();
        if (d && d.running) { setTimeout(poll, 2000); }
        else { setThumbBusy(false); if (d) toast.success(`Miniature aggiornate (${d.with_thumb}/${d.total})`); }
      };
      setTimeout(poll, 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setThumbBusy(false); }
  };


  const setEmailField = (k, v) => setEmail((p) => ({ ...p, [k]: v }));
  const onEmailProvider = (prov) => {
    const info = emailProviders[prov];
    setEmail((p) => ({ ...p, provider: prov, ...(info && prov !== "altro" ? { smtp_host: info.smtp_host, smtp_port: info.smtp_port, smtp_security: info.smtp_security, imap_host: info.imap_host || "", imap_port: info.imap_port || 993 } : {}) }));
  };
  const saveEmail = async () => {
    if (!email.from_email) { toast.error("Inserisci l'indirizzo email mittente"); return; }
    if (!email.smtp_host) { toast.error("Inserisci il server SMTP"); return; }
    if (!email.has_password && !emailPw) { toast.error("Inserisci la password della casella email"); return; }
    setEmailSaving(true);
    try {
      const payload = {
        from_email: email.from_email, from_name: email.from_name, username: email.username || email.from_email, provider: email.provider,
        smtp_host: email.smtp_host, smtp_port: Number(email.smtp_port), smtp_security: email.smtp_security,
        imap_host: email.imap_host || "", imap_port: Number(email.imap_port) || 993, imap_security: email.imap_security || "ssl", enabled: email.enabled,
      };
      if (emailPw) payload.password = emailPw;
      const { data } = await api.put("/studio/email/config", payload);
      setEmail((p) => ({ ...p, ...data }));
      setEmailPw("");
      toast.success("Configurazione Email salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmailSaving(false); }
  };
  const testEmail = async () => {
    setEmailTesting(true); setEmailTest(null);
    try {
      const { data } = await api.post("/studio/email/config/test");
      setEmailTest(data);
      if (data.ok) toast.success("Connessione SMTP riuscita");
      else toast.error("Connessione SMTP fallita — controlla i dati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmailTesting(false); }
  };

  const openDinNew = () => setDinEdit({ id: null, testo: "", tipi: [] });
  const openDinEdit = (d) => setDinEdit({ id: d.id, testo: d.testo, tipi: [...(d.tipi || [])] });
  const toggleDinTipo = (t) => setDinEdit((e) => {
    if (t === "TUTTE") return { ...e, tipi: e.tipi.includes("TUTTE") ? [] : ["TUTTE"] };
    const has = e.tipi.includes(t);
    return { ...e, tipi: has ? e.tipi.filter((x) => x !== t) : [...e.tipi.filter((x) => x !== "TUTTE"), t] };
  });
  const persistDinList = async (list) => {
    const { data } = await api.put("/studio/dinamiche-list", { dinamiche: list });
    setDinList(Array.isArray(data) ? data : []);
  };
  const saveDin = async () => {
    if (!dinEdit.testo.trim()) { toast.error("Inserisci il testo della dinamica"); return; }
    if (!dinEdit.tipi.length) { toast.error("Seleziona almeno una tipologia"); return; }
    setSavingDin(true);
    try {
      const list = dinEdit.id
        ? dinList.map((d) => (d.id === dinEdit.id ? { ...d, testo: dinEdit.testo.trim(), tipi: dinEdit.tipi } : d))
        : [...dinList, { testo: dinEdit.testo.trim(), tipi: dinEdit.tipi }];
      await persistDinList(list);
      setDinEdit(null);
      toast.success("Dinamica salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDin(false); }
  };
  const deleteDin = async (d) => {
    if (!window.confirm("Eliminare questa dinamica?")) return;
    try { await persistDinList(dinList.filter((x) => x.id !== d.id)); toast.success("Dinamica eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [savingAssoc, setSavingAssoc] = useState(false);
  const addAssoc = () => setForm((f) => ({ ...f, studi_associati: [...(f.studi_associati || []), { nome: "", indirizzo: "", citta: "", partita_iva: "", codice_fiscale: "", telefono: "", cellulare: "", email: "", pec: "" }] }));
  const removeAssoc = (i) => setForm((f) => ({ ...f, studi_associati: (f.studi_associati || []).filter((_, x) => x !== i) }));
  const setAssoc = (i, k, v) => setForm((f) => { const arr = [...(f.studi_associati || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, studi_associati: arr }; });
  const saveAssoc = async () => {
    setSavingAssoc(true);
    try { await api.patch("/studio/settings", { studi_associati: form.studi_associati || [] }); toast.success("Studi associati salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingAssoc(false); }
  };

  const [savingMargins, setSavingMargins] = useState(false);
  const PM = form.print_margins || { top: 20, bottom: 15, left: 20, right: 15 };
  const setPM = (k, v) => setForm((f) => ({ ...f, print_margins: { ...(f.print_margins || { top: 20, bottom: 15, left: 20, right: 15 }), [k]: v } }));
  const saveMargins = async () => {
    setSavingMargins(true);
    try { await api.patch("/studio/settings", { print_margins: PM }); invalidatePrintMarginsCache(); toast.success("Margini di stampa salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingMargins(false); }
  };

  const toggleFirmaField = (key) => setForm((f) => {
    const cur = f.firma_fields || [];
    return { ...f, firma_fields: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
  });

  const save = async (e) => {
    e.preventDefault();setSaving(true);
    try {
      await api.patch("/studio/settings", form);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un file immagine"); return; }
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file); });
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
      const maxW = 420;
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const out = canvas.toDataURL("image/png");
      setForm((f) => ({ ...f, logo: out }));
      toast.success("Logo caricato — ricorda di salvare");
    } catch { toast.error("Impossibile caricare il logo"); }
  };

  const [savingPV, setSavingPV] = useState(false);
  const setPV = (i, k, v) => setProformaVoci((a) => a.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addPV = () => setProformaVoci((a) => [...a, { descrizione: "", quantita: 1, prezzo_unitario: 0 }]);
  const delPV = (i) => setProformaVoci((a) => a.filter((_, idx) => idx !== i));
  const savePV = async () => {
    setSavingPV(true);
    const clean = proformaVoci.map((v) => ({ descrizione: (v.descrizione || "").trim(), quantita: Number(v.quantita) || 1, prezzo_unitario: Number(v.prezzo_unitario) || 0 })).filter((v) => v.descrizione);
    try { await api.patch("/studio/settings", { proforma_voci: clean }); setProformaVoci(clean); toast.success("Voci proforma predefinite salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPV(false); }
  };

  const [savingDic, setSavingDic] = useState(false);
  const [fatt, setFatt] = useState({ regime_default: "forfettario", iva_pct: "", ritenuta_pct: "", cassa_pct: "", codice_sdi: "", mostra_filiale: false });
  const [savingFatt, setSavingFatt] = useState(false);
  const setFattField = (k, v) => setFatt((f) => ({ ...f, [k]: v }));
  const saveFatt = async () => {
    setSavingFatt(true);
    const clean = {
      regime_default: fatt.regime_default || "forfettario",
      iva_pct: fatt.iva_pct === "" ? "" : Number(fatt.iva_pct) || 0,
      ritenuta_pct: fatt.ritenuta_pct === "" ? "" : Number(fatt.ritenuta_pct) || 0,
      cassa_pct: fatt.cassa_pct === "" ? "" : Number(fatt.cassa_pct) || 0,
      codice_sdi: (fatt.codice_sdi || "").trim(),
      mostra_filiale: !!fatt.mostra_filiale,
    };
    try { await api.patch("/studio/settings", { fatturazione_defaults: clean }); setFatt(clean); toast.success("Impostazioni fatturazione salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingFatt(false); }
  };
  const genDicId = () => `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const setDic = (i, k, v) => setDiciture((a) => a.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addDic = () => setDiciture((a) => [...a, { id: genDicId(), nome: "", testo: "", regime: "forfettario", principale: false }]);
  const delDic = (i) => setDiciture((a) => a.filter((_, idx) => idx !== i));
  const setPrincipale = (i, val) => setDiciture((a) => a.map((x, idx) => (idx === i ? { ...x, principale: val } : (val && x.regime === a[i].regime ? { ...x, principale: false } : x))));
  const saveDic = async () => {
    setSavingDic(true);
    const clean = diciture.map((d) => ({ id: d.id || genDicId(), nome: (d.nome || "").trim(), testo: (d.testo || "").trim(), regime: d.regime || "forfettario", principale: !!d.principale })).filter((d) => d.nome || d.testo);
    try { await api.patch("/studio/settings", { proforma_diciture: clean }); setDiciture(clean); toast.success("Modelli dicitura salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDic(false); }
  };

  const addConto = () => setConti((c) => [...c, { nome: "", intestatario: "", banca: "", iban: "", bic_swift: "", filiale: "", note: "", predefinito: c.length === 0 }]);
  const removeConto = (i) => setConti((c) => { const b = c.filter((_, idx) => idx !== i); if (b.length && !b.some((x) => x.predefinito)) b[0] = { ...b[0], predefinito: true }; return b; });
  const setContoField = (i, k, v) => setConti((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const setContoDefault = (i) => setConti((c) => c.map((x, idx) => ({ ...x, predefinito: idx === i })));
  const saveConti = async () => {
    setSavingConti(true);
    try {
      const clean = conti
        .map((x) => ({ nome: (x.nome || "").trim(), intestatario: (x.intestatario || "").trim(), banca: (x.banca || "").trim(), iban: (x.iban || "").trim(), bic_swift: (x.bic_swift || "").trim(), filiale: (x.filiale || "").trim(), note: (x.note || "").trim(), predefinito: !!x.predefinito }))
        .filter((x) => x.nome || x.intestatario || x.banca || x.iban);
      if (clean.length && !clean.some((x) => x.predefinito)) clean[0].predefinito = true;
      await api.patch("/studio/settings", { conti_correnti: clean });
      setConti(clean);
      toast.success("Conti correnti salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingConti(false); }
  };

  const toggleReq = (key) => setReqFields((r) => (r.includes(key) ? r.filter((k) => k !== key) : [...r, key]));  const saveReq = async () => {
    setReqSaving(true);
    try {
      await api.patch("/studio/settings", { required_fields: reqFields });
      toast.success("Campi obbligatori salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReqSaving(false);
    }
  };

  const setStato = (i, v) => setStati((s) => s.map((x, idx) => (idx === i ? v : x)));
  const addStato = () => setStati((s) => [...s, ""]);
  const delStato = (i) => setStati((s) => s.filter((_, idx) => idx !== i));
  const setRuolo = (i, v) => setRuoli((s) => s.map((x, idx) => (idx === i ? v : x)));
  const addRuolo = () => setRuoli((s) => [...s, ""]);
  const delRuolo = (i) => setRuoli((s) => s.filter((_, idx) => idx !== i));
  const azSort = (arr) => [...arr].sort((x, y) => (x || "").localeCompare(y || "", "it", { sensitivity: "base" }));
  const reorder = (arr, from, to) => { if (from == null || from === to) return arr; const b = [...arr]; const [m] = b.splice(from, 1); b.splice(to, 0, m); return b; };
  const onStatoDrop = (i) => setStati((a) => reorder(a, dragStato, i));
  const sortStatiAZ = () => setStati((a) => azSort(a));
  const onRuoloDrop = (i) => setRuoli((a) => reorder(a, dragRuolo, i));
  const sortRuoliAZ = () => setRuoli((a) => azSort(a));
  const saveRuoli = async () => {
    setRuoliSaving(true);
    try {
      const clean = ruoli.map((x) => x.trim()).filter(Boolean);
      await api.patch("/studio/settings", { ruolo_sinistro_options: clean });
      setRuoli(clean);
      toast.success("Ruoli sinistro salvati");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setRuoliSaving(false); }
  };

  const toggleCop = (key) => setCopFields((a) => a.map((x) => (x.key === key ? { ...x, enabled: !x.enabled } : x)));
  const setCopStyle = (key, patch) => setCopStyles((s) => ({ ...s, [key]: { ...(s[key] || {}), ...patch } }));
  const onCopDrop = (i) => setCopFields((a) => { if (dragCop == null || dragCop === i) return a; const b = [...a]; const [m] = b.splice(dragCop, 1); b.splice(i, 0, m); return b; });
  const saveCop = async () => {
    setCopSaving(true);
    try {
      const enabled = copFields.filter((x) => x.enabled).map((x) => x.key);
      if (copModelId === "__default__") {
        await api.patch("/studio/settings", { copertina_fields: enabled, copertina_field_styles: copStyles });
        setCopDefault((d) => ({ ...d, fields: enabled, styles: copStyles }));
      } else {
        const models = copModels.map((x) => (x.id === copModelId ? { ...x, fields: enabled, field_styles: copStyles } : x));
        await api.patch("/studio/settings", { copertina_models: models });
        setCopModels(models);
      }
      toast.success("Campi copertina salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCopSaving(false); }
  };
  const applyCopConfig = (enabledKeys, styles) => {
    const ek = Array.isArray(enabledKeys) && enabledKeys.length ? enabledKeys : DEFAULT_COPERTINA_FIELDS;
    setCopFields([
      ...ek.map((k) => COPERTINA_CATALOG.find((c) => c.key === k)).filter(Boolean).map((c) => ({ ...c, enabled: true })),
      ...COPERTINA_CATALOG.filter((c) => !ek.includes(c.key)).map((c) => ({ ...c, enabled: false })),
    ]);
    setCopStyles(styles && typeof styles === "object" ? styles : {});
  };
  const selectCopModel = (id) => {
    setCopModelId(id);
    if (id === "__default__") { applyCopConfig(copDefault.fields, copDefault.styles); setCopTemplate(copDefault.template); }
    else { const m = copModels.find((x) => x.id === id); if (m) { applyCopConfig(m.fields, m.field_styles); setCopTemplate(m.template || ""); } }
  };
  const persistCopModels = async (models) => { await api.patch("/studio/settings", { copertina_models: models }); setCopModels(models); };
  const newCopModel = async () => {
    const nome = (window.prompt("Nome del nuovo modello di copertina (es. Sinistro RCA):") || "").trim();
    if (!nome) return;
    const id = window.crypto?.randomUUID?.() || ("m" + Date.now().toString(36));
    const model = { id, nome, fields: copFields.filter((x) => x.enabled).map((x) => x.key), field_styles: copStyles, template: copTemplate };
    try { await persistCopModels([...copModels, model]); setCopModelId(id); toast.success("Modello creato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameCopModel = async () => {
    if (copModelId === "__default__") return;
    const cur = copModels.find((x) => x.id === copModelId);
    const nome = (window.prompt("Rinomina modello:", cur?.nome || "") || "").trim();
    if (!nome) return;
    try { await persistCopModels(copModels.map((x) => (x.id === copModelId ? { ...x, nome } : x))); toast.success("Modello rinominato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteCopModel = async () => {
    if (copModelId === "__default__") return;
    if (!window.confirm("Eliminare questo modello di copertina?")) return;
    try { await persistCopModels(copModels.filter((x) => x.id !== copModelId)); selectCopModel("__default__"); toast.success("Modello eliminato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCopTemplate = async (contentHtml) => {
    setCopTemplate(contentHtml);
    if (copModelId === "__default__") { await api.patch("/studio/settings", { copertina_template: contentHtml }); setCopDefault((d) => ({ ...d, template: contentHtml })); }
    else { await persistCopModels(copModels.map((x) => (x.id === copModelId ? { ...x, template: contentHtml } : x))); }
  };
  const saveStati = async () => {
    setStatiSaving(true);
    try {
      const clean = stati.map((x) => (x || "").trim()).filter(Boolean);
      await api.patch("/studio/settings", { stato_aggiuntivo_options: clean });
      setStati(clean);
      toast.success("Stati aggiuntivi salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setStatiSaving(false);
    }
  };

  const setTipCode = (i, v) => setTipologie((s) => s.map((x, idx) => (idx === i ? { ...x, code: v } : x)));
  const setTipLabel = (i, v) => setTipologie((s) => s.map((x, idx) => (idx === i ? { ...x, label: v } : x)));
  const setTipColor = (i, v) => setTipologie((s) => s.map((x, idx) => (idx === i ? { ...x, color: v } : x)));
  const addTip = () => setTipologie((s) => [...s, { code: "", label: "", color: "#f59e0b" }]);
  const delTip = (i) => setTipologie((s) => s.filter((_, idx) => idx !== i));
  const onTipDrop = (i) => setTipologie((a) => reorder(a, dragTip, i));
  const sortTipAZ = () => setTipologie((a) => [...a].sort((x, y) => (x.code || "").localeCompare(y.code || "", "it", { sensitivity: "base" })));
  const saveTip = async () => {
    setTipSaving(true);
    try {
      const clean = tipologie.map((x) => ({ code: (x.code || "").trim(), label: (x.label || "").trim(), color: x.color || "", veicolo: x.veicolo !== false, solo_prof: !!x.solo_prof })).filter((x) => x.code);
      await api.patch("/studio/settings", { tipologie_sinistro: clean });
      setTipologie(clean); clearTipologieCache();
      toast.success("Tipologie sinistro salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTipSaving(false); }
  };

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  const tabFromHash = findTab((location.hash || "#sez-dati-studio").slice(1));
  const activeTab = !storageOk && tabFromHash.key === "storage" ? SETTINGS_TABS[0] : tabFromHash;
  const part = activeTab.part;
  const secStyle = `[data-testid="impostazioni-page"] > [id^="sez-"]{display:none}` +
    activeTab.secs.map((id) => `[data-testid="impostazioni-page"] > #${id}{display:block;border-top:3px solid ${activeTab.color}}`).join("");

  return (
    <div data-testid="impostazioni-page">
      <style>{secStyle}</style>
      <PageHeader
        title="Impostazioni Studio"
        subtitle="Dati anagrafici, contatti e carta intestata usati nei documenti e nelle lettere."
        icon={Settings}
      />
      <SettingsTabs active={activeTab} hidden={storageOk ? [] : ["storage"]} onSelect={(t) => navigate(`/app/impostazioni#${t.anchor}`)} />
      <SecurityEmailCard />
      <form onSubmit={save} id="sez-dati-studio" className="pgp-card p-6 max-w-2xl pgp-fade-up space-y-5" data-testid="settings-form">
        <h3 className="font-head font-semibold flex items-center gap-2" style={{ color: activeTab.color }} data-testid="settings-form-title">{activeTab.label}</h3>
        <div className={`grid sm:grid-cols-2 gap-5 ${part !== "dati" ? "!hidden" : ""}`} data-testid="settings-part-dati">
          {FIELDS.map((f) => {
            const invalid = isEmailInvalid(f.key, form[f.key]);
            return (
              <div key={f.key} className={f.top ? "sm:col-span-2" : ""}>
                <Label className="text-slate-300">{f.label}</Label>
                <Input
                  noUppercase
                  value={form[f.key] || ""}
                  onChange={(e) => { if (!f.readOnly) setForm({ ...form, [f.key]: e.target.value }); }}
                  readOnly={f.readOnly}
                  data-testid={`settings-${f.key}`}
                  className={`mt-1.5 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 ${invalid ? "border-red-500/60 focus-visible:ring-red-500/40" : "border-slate-700"} ${f.mono ? "font-mono" : ""} ${f.readOnly ? "opacity-70 cursor-not-allowed text-slate-400" : ""}`}
                />
                {invalid && (
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-red-400" data-testid={`settings-${f.key}-warning`}>
                    <AlertTriangle className="h-3 w-3 shrink-0" /> Formato email non valido (es. nome@dominio.it)
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div id="sez-intestazione" className={`space-y-5 ${part !== "intestazione" ? "!hidden" : ""}`} data-testid="settings-part-intestazione">
        <div>
          <div>
            <Label className="text-slate-300">Posizione intestazione (lettere ed email)</Label>
            <div className="mt-1.5 inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="settings-intestazione-align">
              {[["left", "Sinistra"], ["center", "Centro"], ["right", "Destra"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, intestazione_align: v })}
                  data-testid={`settings-intestazione-align-${v}`}
                  className={`px-4 py-1.5 text-sm transition-colors ${(form.intestazione_align || "left") === v ? "bg-amber-500 text-slate-950 font-medium" : "bg-slate-900/60 text-slate-300 hover:bg-slate-800"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-slate-300">Intestazione personalizzata (editor visuale)</Label>
            <div className="mt-1.5">
              <IntestazioneEditor value={form.intestazione_custom_html || ""} studio={form}
                onChange={(html) => setForm((f) => ({ ...f, intestazione_custom_html: html }))} />
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-5">
          <Label className="text-slate-200 text-base">Logo studio</Label>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">Mostrato in cima alle fatture proforma e ai documenti. PNG/JPG, ridimensionato automaticamente.</p>
          <div className="flex items-center gap-4">
            <div className="h-20 w-40 rounded-lg border border-slate-700 bg-white/90 flex items-center justify-center overflow-hidden shrink-0" data-testid="settings-logo-preview">
              {form.logo ? (
                <img src={form.logo} alt="Logo studio" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-[11px] text-slate-500">Nessun logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-600 px-3 py-2 text-sm text-slate-200 w-fit" data-testid="settings-logo-upload">
                <ImageIcon className="h-4 w-4" /> {form.logo ? "Sostituisci logo" : "Carica logo"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="settings-logo-input" />
              </label>
              {form.logo && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, logo: "" }))} data-testid="settings-logo-remove" className="text-xs text-red-400 hover:text-red-300 w-fit">Rimuovi logo</button>
              )}
            </div>
          </div>
        </div>
        </div>
        <div id="sez-firma" className={part !== "firma" ? "!hidden" : ""} data-testid="settings-part-firma">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-slate-200 text-base">Firma Email</Label>
              <p className="text-xs text-slate-500 mt-0.5">Firma automatica aggiunta alle email inviate dal gestionale.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={form.firma_enabled !== false} onCheckedChange={(v) => setForm({ ...form, firma_enabled: v })} data-testid="settings-firma-enabled" className="data-[state=checked]:bg-amber-500" />
              <Label className="text-slate-300 text-sm">Includi firma nelle email</Label>
            </div>
          </div>

          {form.firma_enabled !== false && (
            <>
              <p className="text-xs text-slate-400 mt-4 mb-2">Seleziona i campi da includere nella firma automatica:</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {FIRMA_FIELDS.map((fld) => {
                  const has = firmaHasData(fld.key, form);
                  const checked = (form.firma_fields || []).includes(fld.key);
                  return (
                    <label key={fld.key} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 cursor-pointer hover:bg-slate-800/40" data-testid={`firma-field-${fld.key}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleFirmaField(fld.key)} className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                      <span className="text-sm text-slate-200 flex-1">{fld.label}</span>
                      {!has && <span className="text-[10px] text-slate-500 font-mono">nessun dato</span>}
                    </label>
                  );
                })}
              </div>

              <div className="mt-4">
                <Label className="text-slate-300 text-sm">Firma personalizzata (opzionale)</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-1.5">Se compilata, sovrascrive i campi selezionati sopra.</p>
                <Textarea
                  value={form.firma_email || ""}
                  onChange={(e) => setForm({ ...form, firma_email: e.target.value })}
                  data-testid="settings-firma-email"
                  rows={3}
                  className="bg-slate-900/60 border-slate-700"
                  placeholder="Lascia vuoto per usare i campi selezionati sopra"
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300 text-sm">Anteprima firma</Label>
                <div className="mt-1.5 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-300 whitespace-pre-wrap min-h-[60px]" data-testid="firma-preview">
                  {composeFirma(form) || <span className="text-slate-600">Nessun campo selezionato.</span>}
                </div>
              </div>
            </>
          )}
        </div>
        <Button type="submit" disabled={saving} data-testid="save-settings-button"
          className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Salva impostazioni
        </Button>
      </form>

      <div id="sez-studi-associati" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="studi-associati">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100">Studi Associati</h3>
            <p className="text-sm text-slate-400 mt-1">Altre sedi/studi selezionabili come intestazione nelle lettere.</p>
          </div>
          <Button type="button" onClick={addAssoc} data-testid="assoc-add" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi studio
          </Button>
        </div>
        <div className="mt-4 space-y-4">
          {(form.studi_associati || []).length === 0 && <p className="text-sm text-slate-500">Nessuno studio associato.</p>}
          {(form.studi_associati || []).map((a, i) => (
            <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid={`assoc-${i}`}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs font-mono text-slate-500">Studio #{i + 1}</span>
                <Button type="button" variant="ghost" onClick={() => removeAssoc(i)} data-testid={`assoc-remove-${i}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {ASSOC_FIELDS.map((fld) => (
                  <div key={fld.key} className={fld.full ? "sm:col-span-2" : ""}>
                    <Label className="text-slate-300 text-xs">{fld.label}</Label>
                    <Input noUppercase value={a[fld.key] || ""} onChange={(e) => setAssoc(i, fld.key, e.target.value)} data-testid={`assoc-${i}-${fld.key}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button type="button" onClick={saveAssoc} disabled={savingAssoc} data-testid="assoc-save" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {savingAssoc ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva studi associati
        </Button>
      </div>

      <div id="sez-conti-correnti" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="conti-correnti">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100">Conti Correnti</h3>
            <p className="text-sm text-slate-400 mt-1">Coordinate bancarie dello studio, disponibili come variabili nelle lettere ({"{{studio.iban}}"}, {"{{studio.banca}}"}, {"{{studio.intestatario_conto}}"}) e nelle fatture proforma.</p>
          </div>
          <Button type="button" onClick={addConto} data-testid="conto-add" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi conto
          </Button>
        </div>
        <div className="mt-4 space-y-4">
          {conti.length === 0 && <p className="text-sm text-slate-500" data-testid="conti-empty">Nessun conto corrente. Aggiungine uno.</p>}
          {conti.map((c, i) => (
            <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid={`conto-${i}`}>
              <div className="flex justify-between items-center mb-3">
                <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid={`conto-default-${i}`}>
                  <input type="radio" name="conto-default" checked={!!c.predefinito} onChange={() => setContoDefault(i)} className="accent-amber-500" />
                  <span className={c.predefinito ? "text-amber-400 font-semibold" : "text-slate-400"}>{c.predefinito ? "Predefinito" : "Imposta come predefinito"}</span>
                </label>
                <Button type="button" variant="ghost" onClick={() => removeConto(i)} data-testid={`conto-remove-${i}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {CONTO_FIELDS.map((fld) => (
                  <div key={fld.key} className={fld.full ? "sm:col-span-2" : ""}>
                    <Label className="text-slate-300 text-xs">{fld.label}</Label>
                    <Input noUppercase value={c[fld.key] || ""} onChange={(e) => setContoField(i, fld.key, e.target.value)} onBlur={fld.key === "iban" ? () => setIbanBlurred((b) => ({ ...b, [i]: true })) : undefined} data-testid={`conto-${i}-${fld.key}`} className={`mt-1 h-9 bg-slate-900/60 border-slate-700 ${fld.mono ? "font-mono" : ""}`} />
                    {fld.key === "iban" && <ValidationHint result={validateIBAN(c.iban)} show={!!ibanBlurred[i]} testId={`conto-${i}-iban`} />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button type="button" onClick={saveConti} disabled={savingConti} data-testid="conti-save" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {savingConti ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva conti correnti
        </Button>
      </div>

      <div id="sez-impostazioni-stampe" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="impostazioni-stampe">
        <h3 className="font-head font-semibold text-slate-100">Impostazioni Stampe</h3>
        <p className="text-sm text-slate-400 mt-1">Margini pagina applicati come default a tutti i PDF del gestionale (lettere, copertina). Nell'editor lettere puoi sovrascriverli temporaneamente con il controllo "MARGINI MM".</p>
        <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-8 items-start">
          <div className="grid grid-cols-2 gap-4">
            {[["top", "Margine superiore"], ["bottom", "Margine inferiore"], ["left", "Margine sinistro"], ["right", "Margine destro"]].map(([k, label]) => (
              <div key={k}>
                <Label className="text-slate-300 text-sm">{label} (mm)</Label>
                <Input type="number" min="0" max="60" value={PM[k]} onChange={(e) => setPM(k, Number(e.target.value) || 0)} data-testid={`print-margin-${k}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
            ))}
            <div className="col-span-2">
              <Button type="button" onClick={saveMargins} disabled={savingMargins} data-testid="print-margins-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {savingMargins ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva margini
              </Button>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative bg-white border border-slate-500 shadow-lg" style={{ width: "210px", height: "297px" }} data-testid="print-margins-preview">
              <div className="absolute border-2 border-dashed border-amber-500 bg-amber-500/10" style={{ top: `${PM.top}px`, bottom: `${PM.bottom}px`, left: `${PM.left}px`, right: `${PM.right}px` }} />
              <span className="absolute left-1/2 -translate-x-1/2 text-[10px] font-mono text-slate-600" style={{ top: "3px" }}>{PM.top} mm</span>
              <span className="absolute left-1/2 -translate-x-1/2 text-[10px] font-mono text-slate-600" style={{ bottom: "3px" }}>{PM.bottom} mm</span>
              <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-600" style={{ left: "3px", writingMode: "vertical-rl" }}>{PM.left} mm</span>
              <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-600" style={{ right: "3px", writingMode: "vertical-rl" }}>{PM.right} mm</span>
            </div>
          </div>
        </div>
      </div>

      <div id="sez-preferenze" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="preferenze-utente">
        <h3 className="font-head font-semibold text-slate-100">Preferenze Utente</h3>
        <p className="text-sm text-slate-400 mt-1 mb-4">Colore accento dell'interfaccia.</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(ACCENTS).map(([key, a]) => (
            <button key={key} type="button" onClick={() => setAccent(key)} data-testid={`accent-${key}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${accent === key ? "border-slate-400 bg-slate-800/60" : "border-slate-700 hover:bg-slate-800/40"}`}>
              <span className="h-5 w-5 rounded-full" style={{ background: a.c500 }} />
              <span className="text-sm text-slate-200">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div id="sez-dinamiche" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="dinamiche-manager">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100">Gestione Dinamiche Sinistro</h3>
            <p className="text-sm text-slate-400 mt-1">Dinamiche preimpostate per il campo Dinamica/Descrizione della pratica. Compaiono in "Scegli dinamica" filtrate per tipologia del sinistro.</p>
          </div>
          <Button type="button" onClick={openDinNew} data-testid="din-add" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi dinamica
          </Button>
        </div>
        <div className="mt-4 divide-y divide-slate-800 border border-slate-800 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 bg-slate-900/50 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
            <span>Testo dinamica</span><span>Tipologia</span><span>Azioni</span>
          </div>
          {dinList.length === 0 ? (
            <p className="text-sm text-slate-500 px-3 py-4">Nessuna dinamica. Aggiungine una con il pulsante sopra.</p>
          ) : dinList.map((d) => (
            <div key={d.id} className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2.5 items-center" data-testid={`din-row-${d.id}`}>
              <span className="text-sm text-slate-200 line-clamp-2" title={d.testo}>{d.testo}</span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
                {(d.tipi || []).map((t) => (
                  <span key={t} className="text-[9px] font-semibold rounded border border-amber-500/30 text-amber-400 bg-amber-500/10 px-1.5 py-0.5">{t}</span>
                ))}
              </div>
              <div className="flex gap-1 shrink-0">
                <button type="button" onClick={() => openDinEdit(d)} data-testid={`din-edit-${d.id}`} title="Modifica" className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-700 text-slate-300 hover:text-amber-400 hover:border-amber-500/40"><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => deleteDin(d)} data-testid={`din-del-${d.id}`} title="Elimina" className="h-8 w-8 flex items-center justify-center rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!dinEdit} onOpenChange={(o) => !o && setDinEdit(null)}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="din-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">{dinEdit?.id ? "Modifica dinamica" : "Nuova dinamica"}</DialogTitle></DialogHeader>
          {dinEdit && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Descrizione dinamica</Label>
                <Textarea value={dinEdit.testo} onChange={(e) => setDinEdit({ ...dinEdit, testo: e.target.value })} rows={4} data-testid="din-edit-text" className="mt-1.5 bg-slate-900/60 border-slate-700" placeholder="Descrivi la dinamica del sinistro…" />
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">Tipologia sinistro associata</Label>
                <div className="flex flex-wrap gap-1.5">
                  {["TUTTE", ...TIPI_SINISTRO].map((t) => (
                    <button key={t} type="button" onClick={() => toggleDinTipo(t)} data-testid={`din-tipo-${t}`}
                      className={`text-xs font-semibold rounded-md border px-2.5 py-1.5 transition-colors ${dinEdit.tipi.includes(t) ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" onClick={saveDin} disabled={savingDin} data-testid="din-edit-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {savingDin ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {pec && (
        <div id="sez-pec" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="pec-config">
          {isDemo && demoMsg}
          <fieldset disabled={isDemo} className={isDemo ? "opacity-60 pointer-events-none" : ""}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><MailCheck className="h-4 w-4 text-amber-400" /> PEC — Posta Elettronica Certificata</h3>
              <p className="text-sm text-slate-400 mt-1">Invio reale di PEC dalla casella dello studio. La password viene cifrata e mai mostrata.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 pt-1">
              <span className="text-xs text-slate-400">Attiva</span>
              <Switch checked={!!pec.enabled} onCheckedChange={(v) => setPecField("enabled", v)} data-testid="pec-enabled" className="data-[state=checked]:bg-amber-500" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Provider</Label>
              <Select value={pec.provider} onValueChange={onProvider}>
                <SelectTrigger data-testid="pec-provider" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                  {Object.entries(pecProviders).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Indirizzo casella PEC</Label>
              <Input value={pec.mailbox} onChange={(e) => setPecField("mailbox", e.target.value)} placeholder="studio@pec.aruba.it" data-testid="pec-mailbox" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Username <span className="text-slate-500 font-normal">(di norma = indirizzo)</span></Label>
              <Input value={pec.username} onChange={(e) => setPecField("username", e.target.value)} placeholder="studio@pec.aruba.it" data-testid="pec-username" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Password {pec.has_password && <span className="text-emerald-400 font-normal text-xs">· impostata (lascia vuoto per non cambiarla)</span>}</Label>
              <Input type="password" value={pecPw} onChange={(e) => setPecPw(e.target.value)} placeholder={pec.has_password ? "•••••••• (invariata)" : "Password / password per app"} data-testid="pec-password" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Server SMTP (invio)</Label>
              <Input value={pec.smtp_host} onChange={(e) => setPecField("smtp_host", e.target.value)} data-testid="pec-smtp-host" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Porta</Label>
                <Input value={pec.smtp_port} onChange={(e) => setPecField("smtp_port", e.target.value)} data-testid="pec-smtp-port" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-slate-300">TLS</Label>
                <Select value={pec.smtp_security} onValueChange={(v) => setPecField("smtp_security", v)}>
                  <SelectTrigger data-testid="pec-smtp-security" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100"><SelectItem value="ssl">SSL</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Server IMAP (ricevute)</Label>
              <Input value={pec.imap_host} onChange={(e) => setPecField("imap_host", e.target.value)} data-testid="pec-imap-host" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Porta</Label>
                <Input value={pec.imap_port} onChange={(e) => setPecField("imap_port", e.target.value)} data-testid="pec-imap-port" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-slate-300">TLS</Label>
                <Select value={pec.imap_security} onValueChange={(v) => setPecField("imap_security", v)}>
                  <SelectTrigger data-testid="pec-imap-security" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100"><SelectItem value="ssl">SSL</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {pecTest && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm" data-testid="pec-test-result">
              <span className={`flex items-center gap-1.5 ${pecTest.smtp ? "text-emerald-400" : "text-red-400"}`}>
                {pecTest.smtp ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} SMTP {pecTest.smtp ? "OK" : "KO"}
                {!pecTest.smtp && pecTest.smtp_error && <span className="text-slate-500 text-xs">({pecTest.smtp_error})</span>}
              </span>
              <span className={`flex items-center gap-1.5 ${pecTest.imap ? "text-emerald-400" : "text-red-400"}`}>
                {pecTest.imap ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} IMAP {pecTest.imap ? "OK" : "KO"}
                {!pecTest.imap && pecTest.imap_error && <span className="text-slate-500 text-xs">({pecTest.imap_error})</span>}
              </span>
            </div>
          )}

          <div className="flex gap-2 mt-5">
            <Button onClick={savePec} disabled={pecSaving} data-testid="pec-save-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {pecSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva PEC
            </Button>
            <Button variant="outline" onClick={testPec} disabled={pecTesting || !pec.configured} data-testid="pec-test-button" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              {pecTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MailCheck className="h-4 w-4 mr-1" />} Verifica connessione
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">Le ricevute di accettazione e consegna vengono controllate automaticamente ogni 15 minuti; puoi anche aggiornarle manualmente dallo storico invii nella scheda pratica.</p>
          </fieldset>
        </div>
      )}

      {storageOk && storage && (
        <div id="sez-storage" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="storage-config">
          <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setOpenStorage((o) => !o)} data-testid="storage-toggle">
            <div className="flex items-start gap-2">
              {openStorage ? <ChevronDown className="h-4 w-4 text-slate-400 mt-1 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 mt-1 shrink-0" />}
              <div>
                <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className={`h-4 w-4 ${storage.provider === "gcs" ? (storage.gcs_ready ? "text-emerald-400" : "text-slate-500") : storage.provider === "wasabi" ? (storage.wasabi_ready ? "text-emerald-400" : "text-slate-500") : "text-emerald-400"}`} /> Storage Allegati</h3>
                <p className="text-sm text-slate-400 mt-1">Scegli dove salvare i file caricati (allegati, foto, documenti, video) per questo studio.</p>
              </div>
            </div>
            <span className={`shrink-0 text-[10px] font-mono px-2 py-1 rounded border ${(storage.provider === "emergent" || (storage.provider === "gcs" && storage.gcs_ready) || (storage.provider === "wasabi" && storage.wasabi_ready)) ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-slate-600 text-slate-400"}`} data-testid="storage-status">
              {storage.provider === "emergent" ? "ATTIVO · Emergent" : storage.provider === "gcs" ? (storage.gcs_ready ? "ATTIVO · Google Cloud" : "NON CONFIGURATO") : storage.provider === "wasabi" ? (storage.wasabi_ready ? "ATTIVO · Wasabi" : "NON CONFIGURATO") : "NON CONFIGURATO"}
            </span>
          </div>

          {openStorage && (<>
          <div className="flex flex-col gap-2 mt-4">
            <button type="button" onClick={() => changeStorageProvider("emergent")} data-testid="storage-emergent"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left ${storage.provider === "emergent" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
              <span className={`h-3.5 w-3.5 rounded-full border ${storage.provider === "emergent" ? "border-amber-400 bg-amber-400" : "border-slate-500"}`} /> Storage Predefinito Emergent (5GB)
            </button>
            <button type="button" onClick={() => changeStorageProvider("gcs")} data-testid="storage-gcs"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left ${storage.provider === "gcs" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
              <span className={`h-3.5 w-3.5 rounded-full border ${storage.provider === "gcs" ? "border-amber-400 bg-amber-400" : "border-slate-500"}`} /> Google Cloud Storage
            </button>
            <button type="button" onClick={() => changeStorageProvider("wasabi")} data-testid="storage-wasabi"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left ${storage.provider === "wasabi" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
              <span className={`h-3.5 w-3.5 rounded-full border ${storage.provider === "wasabi" ? "border-amber-400 bg-amber-400" : "border-slate-500"}`} /> Wasabi
            </button>
          </div>

          {storage.provider === "gcs" && (
            <div className="mt-4 space-y-3">
              {storage.gcs_ready && <p className="text-xs text-emerald-400">Bucket attivo: <strong>{storage.bucket_name}</strong> · Project: <strong>{storage.project_id}</strong></p>}
              <div><Label className="text-slate-300">ID progetto</Label><Input value={storage.project_id || ""} onChange={(e) => setStorage((s) => ({ ...s, project_id: e.target.value }))} data-testid="storage-project" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>
              <div><Label className="text-slate-300">Bucket Name</Label><Input value={storage.bucket_name || ""} onChange={(e) => setStorage((s) => ({ ...s, bucket_name: e.target.value }))} data-testid="storage-bucket" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>
              <div><Label className="text-slate-300">Service Account Key (JSON) {storage.has_key && <span className="text-emerald-400 text-xs font-normal">· impostata (lascia vuoto per non cambiarla)</span>}</Label>
                <Textarea value={gcsJson} onChange={(e) => setGcsJson(e.target.value)} rows={6} data-testid="storage-json" placeholder='{ "type": "service_account", ... }' className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-xs" /></div>
            </div>
          )}

          {storage.provider === "wasabi" && (
            <div className="mt-4 space-y-3" data-testid="storage-wasabi-form">
              {storage.wasabi_ready && <p className="text-xs text-emerald-400">Bucket attivo: <strong>{storage.bucket_name}</strong> · Endpoint: <strong>{storage.wasabi_endpoint}</strong></p>}
              <div><Label className="text-slate-300">Nome del bucket</Label><Input noUppercase value={storage.bucket_name || ""} onChange={(e) => setStorage((s) => ({ ...s, bucket_name: e.target.value }))} data-testid="storage-wasabi-bucket" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>
              <div><Label className="text-slate-300">Chiave di accesso (Access Key) {storage.has_wasabi_access_key && <span className="text-emerald-400 text-xs font-normal">· impostata (lascia vuoto per non cambiarla)</span>}</Label>
                <Input noUppercase value={wasabiAccessKey} onChange={(e) => setWasabiAccessKey(e.target.value)} data-testid="storage-wasabi-access" placeholder={storage.has_wasabi_access_key ? "••••••••" : ""} className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-xs" /></div>
              <div><Label className="text-slate-300">Chiave segreta (Secret Key) {storage.has_wasabi_secret_key && <span className="text-emerald-400 text-xs font-normal">· impostata (lascia vuoto per non cambiarla)</span>}</Label>
                <Input noUppercase type="password" value={wasabiSecretKey} onChange={(e) => setWasabiSecretKey(e.target.value)} data-testid="storage-wasabi-secret" placeholder={storage.has_wasabi_secret_key ? "••••••••" : ""} className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-xs" /></div>
              <div><Label className="text-slate-300">Endpoint</Label><Input noUppercase value={storage.wasabi_endpoint || ""} onChange={(e) => setStorage((s) => ({ ...s, wasabi_endpoint: e.target.value }))} data-testid="storage-wasabi-endpoint" placeholder="es. s3.eu-central-1.wasabisys.com" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-xs" /></div>
              <div><Label className="text-slate-300">Regione</Label><Input noUppercase value={storage.wasabi_region || ""} onChange={(e) => setStorage((s) => ({ ...s, wasabi_region: e.target.value }))} data-testid="storage-wasabi-region" placeholder="es. eu-central-1" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-xs" /></div>
            </div>
          )}

          {storageTest && (
            <div className="mt-3 text-sm" data-testid="storage-test-result">
              <span className={`flex items-center gap-1.5 ${storageTest.ok ? "text-emerald-400" : "text-red-400"}`}>
                {storageTest.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {storageTest.ok ? `Connesso al bucket ${storageTest.bucket}` : (storageTest.error || "Errore")}
              </span>
            </div>
          )}

          <div className="flex gap-2 mt-5">
            <Button onClick={saveStorage} disabled={storageSaving} data-testid="storage-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {storageSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} {storage.provider === "emergent" ? "Salva" : "Salva credenziali"}
            </Button>
            {storage.provider === "gcs" && (
              <Button variant="outline" onClick={testStorage} disabled={storageTesting || !storage.gcs_ready} data-testid="storage-test" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                {storageTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Verifica connessione
              </Button>
            )}
            {storage.provider === "wasabi" && (
              <Button variant="outline" onClick={testStorage} disabled={storageTesting || !storage.wasabi_ready} data-testid="storage-test-wasabi" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                {storageTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Verifica connessione
              </Button>
            )}
          </div>
          </>)}
        </div>
      )}

      {storageOk && usage && (
        <div id="sez-storage-usage" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="storage-usage">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="font-head font-semibold text-foreground flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-amber-400" /> Spazio occupato dagli allegati
            </h3>
            <Button onClick={refreshUsage} disabled={usageRefreshing} data-testid="usage-refresh-btn" variant="outline"
              className="h-8 shrink-0 border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
              {usageRefreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-5">Riepilogo dello spazio usato dai file della pratica, suddiviso per destinazione di archiviazione. La configurazione dello storage è centralizzata dal superadmin.</p>

          <div className="rounded-lg border border-border bg-muted/40 p-4" data-testid="usage-emergent">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-foreground font-medium"><HardDrive className="h-4 w-4 text-amber-400" /> Storage Emergent</span>
              <span className="font-mono text-amber-500" data-testid="usage-emergent-bytes">{fmtBytes(usage.emergent_bytes)}</span>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, (usage.emergent_bytes / (usage.emergent_limit_bytes || 1)) * 100).toFixed(1)}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{usage.emergent_count} file</span>
              <span>Limite {fmtBytes(usage.emergent_limit_bytes)}</span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 mt-4" data-testid="usage-gcs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-foreground font-medium"><Cloud className="h-4 w-4 text-cyan-400" /> Google Cloud Storage</span>
              <span className="font-mono text-cyan-500" data-testid="usage-gcs-bytes">{fmtBytes(usage.gcs_bytes)}</span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">{usage.gcs_count} file{usage.gcs_count === 0 ? " · nessun file su Google Cloud" : ""}</div>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 mt-4" data-testid="usage-wasabi">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-foreground font-medium"><Cloud className="h-4 w-4 text-green-500" /> Wasabi</span>
              <span className="font-mono text-green-500" data-testid="usage-wasabi-bytes">{fmtBytes(usage.wasabi_bytes || 0)}</span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">{usage.wasabi_count || 0} file{(usage.wasabi_count || 0) === 0 ? " · nessun file su Wasabi" : ""}</div>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm">
            <span className="text-foreground font-medium">Totale occupato</span>
            <span className="font-mono text-foreground font-semibold" data-testid="usage-total-bytes">{fmtBytes(usage.total_bytes)}</span>
          </div>

          {Array.isArray(usage.per_pratica) && usage.per_pratica.length > 0 && (
            <div className="mt-6" data-testid="usage-per-pratica">
              <button type="button" onClick={() => setOpenPerPratica((o) => !o)} data-testid="usage-per-pratica-toggle" className="flex items-center gap-2 text-sm font-head font-semibold text-slate-200 mb-3 w-full text-left hover:text-slate-100">
                {openPerPratica ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />} Pratiche che occupano più spazio
              </button>
              {openPerPratica && (
              <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
                {usage.per_pratica.map((p) => {
                  const max = usage.per_pratica[0].bytes || 1;
                  return (
                    <div key={p.pratica_id} className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={`usage-pratica-${p.pratica_id}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="truncate pr-3">
                          <span className="font-mono text-amber-400">{p.numero_pratica}</span>
                          {p.nome_assistito ? <span className="text-slate-300"> · {p.nome_assistito}</span> : null}
                        </span>
                        <span className="font-mono text-slate-200 shrink-0">{fmtBytes(p.bytes)}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full bg-cyan-500/70 rounded-full" style={{ width: `${Math.max(3, (p.bytes / max) * 100).toFixed(1)}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">{p.count} file</div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {storageOk && (
      <div id="sez-thumbs" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="thumbs-config">
        <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-amber-400" /> Miniature allegati
        </h3>
        <p className="text-sm text-slate-400 mt-1 mb-4">Le anteprime di foto e PDF vengono generate automaticamente al caricamento. Se alcune non compaiono (allegati caricati prima di questa funzione), rigenerale qui.</p>
        {thumbStatus && (
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4" data-testid="thumbs-status">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Allegati con anteprima</span>
              <span className="font-mono text-amber-400" data-testid="thumbs-count">{thumbStatus.with_thumb}/{thumbStatus.total}</span>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${thumbStatus.total ? Math.min(100, (thumbStatus.with_thumb / thumbStatus.total) * 100).toFixed(1) : 0}%` }} />
            </div>
            <div className="mt-2 text-xs text-slate-500" data-testid="thumbs-missing">
              {thumbStatus.missing > 0 ? `${thumbStatus.missing} da generare` : "Tutte le anteprime sono generate"}
            </div>
          </div>
        )}
        <Button onClick={rebuildThumbs} disabled={thumbBusy || thumbStatus?.running} data-testid="thumbs-rebuild-btn" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {(thumbBusy || thumbStatus?.running) ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rigenerazione in corso…</> : <>Rigenera miniature</>}
        </Button>
      </div>
      )}

      {email && (
        <div id="sez-email" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="email-config">
          {isDemo && demoMsg}
          <fieldset disabled={isDemo} className={isDemo ? "opacity-60 pointer-events-none" : ""}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Mail className="h-4 w-4 text-amber-400" /> Configurazione Email (SMTP)</h3>
              <p className="text-sm text-slate-400 mt-1">Credenziali SMTP per l'invio di email standard dallo studio. La password viene cifrata e mai mostrata.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 pt-1">
              <span className="text-xs text-slate-400">Attiva</span>
              <Switch checked={!!email.enabled} onCheckedChange={(v) => setEmailField("enabled", v)} data-testid="email-enabled" className="data-[state=checked]:bg-amber-500" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Provider</Label>
              <Select value={email.provider} onValueChange={onEmailProvider}>
                <SelectTrigger data-testid="email-provider" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                  {Object.entries(emailProviders).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Email mittente</Label>
              <Input value={email.from_email} onChange={(e) => setEmailField("from_email", e.target.value)} placeholder="studio@email.it" data-testid="email-from" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Nome mittente <span className="text-slate-500 font-normal">(opzionale)</span></Label>
              <Input value={email.from_name} onChange={(e) => setEmailField("from_name", e.target.value)} placeholder="Studio Rossi" data-testid="email-fromname" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Username <span className="text-slate-500 font-normal">(di norma = email)</span></Label>
              <Input value={email.username} onChange={(e) => setEmailField("username", e.target.value)} placeholder="studio@email.it" data-testid="email-username" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Password {email.has_password && <span className="text-emerald-400 font-normal text-xs">· impostata (lascia vuoto per non cambiarla)</span>}</Label>
              <Input type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} placeholder={email.has_password ? "•••••••• (invariata)" : "Password / password per app"} data-testid="email-password" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Server SMTP</Label>
              <Input value={email.smtp_host} onChange={(e) => setEmailField("smtp_host", e.target.value)} placeholder="smtp.email.it" data-testid="email-smtp-host" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Porta</Label>
                <Input value={email.smtp_port} onChange={(e) => setEmailField("smtp_port", e.target.value)} data-testid="email-smtp-port" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-slate-300">TLS</Label>
                <Select value={email.smtp_security} onValueChange={(v) => setEmailField("smtp_security", v)}>
                  <SelectTrigger data-testid="email-smtp-security" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100"><SelectItem value="ssl">SSL</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="sm:col-span-2 border-t border-slate-800 pt-4 mt-1">
              <p className="text-xs text-slate-400">Ricezione posta in arrivo (IMAP) — necessaria per la casella "In arrivo" del client Email. Lascia vuoto per disattivare la ricezione.</p>
            </div>
            <div>
              <Label className="text-slate-300">Server IMAP</Label>
              <Input value={email.imap_host} onChange={(e) => setEmailField("imap_host", e.target.value)} placeholder="imap.email.it" data-testid="email-imap-host" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Porta IMAP</Label>
                <Input value={email.imap_port} onChange={(e) => setEmailField("imap_port", e.target.value)} data-testid="email-imap-port" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-slate-300">TLS IMAP</Label>
                <Select value={email.imap_security} onValueChange={(v) => setEmailField("imap_security", v)}>
                  <SelectTrigger data-testid="email-imap-security" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100"><SelectItem value="ssl">SSL</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {emailTest && (
            <div className="mt-4 text-sm" data-testid="email-test-result">
              <span className={`flex items-center gap-1.5 ${emailTest.smtp ? "text-emerald-400" : "text-red-400"}`}>
                {emailTest.smtp ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} SMTP {emailTest.smtp ? "OK" : "KO"}
                {!emailTest.smtp && emailTest.smtp_error && <span className="text-slate-500 text-xs">({emailTest.smtp_error})</span>}
              </span>
            </div>
          )}
          <div className="flex gap-2 mt-5">
            <Button onClick={saveEmail} disabled={emailSaving} data-testid="email-save-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {emailSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva Email
            </Button>
            <Button variant="outline" onClick={testEmail} disabled={emailTesting || !email.configured} data-testid="email-test-button" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              {emailTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MailCheck className="h-4 w-4 mr-1" />} Verifica connessione
            </Button>
          </div>
          </fieldset>
        </div>
      )}

      <div id="sez-campi-obbligatori" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="campi-obbligatori-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Settings className="h-4 w-4 text-amber-400" /> Campi Obbligatori Pratica</h3>
            <p className="text-sm text-slate-400 mt-1">Seleziona i campi del wizard "Nuova Pratica" che devono essere compilati obbligatoriamente. Se un campo obbligatorio è vuoto, il wizard blocca l'avanzamento.</p>
          </div>
          <Button type="button" onClick={saveReq} disabled={reqSaving} data-testid="req-save" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {reqSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
          </Button>
        </div>
        <div className="mt-4 space-y-5">
          {REQ_GROUPS.map((g) => (
            <div key={g}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">{g}</div>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {REQUIREABLE_FIELDS.filter((f) => f.group === g).map((f) => (
                  <label key={f.key} data-testid={`req-item-${f.key}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2.5 cursor-pointer hover:bg-slate-800/40">
                    <span className="text-sm text-slate-200">{f.label}</span>
                    <Switch checked={reqFields.includes(f.key)} onCheckedChange={() => toggleReq(f.key)} data-testid={`req-switch-${f.key}`} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div id="sez-stati-aggiuntivi" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="stati-aggiuntivi-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Settings className="h-4 w-4 text-amber-400" /> Stati Aggiuntivi</h3>
            <p className="text-sm text-slate-400 mt-1">Elenco usato dal campo "Stato Aggiuntivo" nelle pratiche. Aggiungi, modifica o elimina le voci.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 mr-1 cursor-pointer" title="Se attivo, converte in maiuscolo durante la digitazione">
              <Switch checked={statiUpper} onCheckedChange={setStatiUpper} data-testid="stati-upper-toggle" className="data-[state=checked]:bg-amber-500" />
              <span className="text-xs text-slate-400">Solo maiuscolo</span>
            </label>
            <Button type="button" variant="outline" onClick={sortStatiAZ} data-testid="stati-sort-az" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowDownAZ className="h-4 w-4 mr-1" /> Ordina A-Z
            </Button>
            <Button type="button" onClick={saveStati} disabled={statiSaving} data-testid="stati-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {statiSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
            </Button>
          </div>
        </div>
        <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-1">
          {stati.map((s, i) => (
            <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={() => onStatoDrop(i)}
              className="flex items-center gap-2" data-testid={`stato-item-${i}`}>
              <span draggable onDragStart={() => setDragStato(i)} onDragEnd={() => setDragStato(null)} title="Trascina per riordinare"
                data-testid={`stato-drag-${i}`} className="shrink-0 cursor-move select-none text-slate-500 hover:text-slate-300 px-1 text-lg leading-none">⠿</span>
              <Input value={s} noUppercase onChange={(e) => setStato(i, statiUpper ? e.target.value.toUpperCase() : e.target.value)} data-testid={`stato-input-${i}`} className="h-9 bg-slate-900/60 border-slate-700" />
              <button type="button" onClick={() => delStato(i)} data-testid={`stato-del-${i}`} title="Elimina" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" onClick={addStato} data-testid="stato-add" className="mt-3 border-slate-700 text-slate-200 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Aggiungi voce</Button>
      </div>

      <div id="sez-ruoli-sinistro" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="ruoli-sinistro-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Settings className="h-4 w-4 text-amber-400" /> Ruoli Sinistro</h3>
            <p className="text-sm text-slate-400 mt-1">Elenco usato dal campo "Ruolo Sinistro" dell'assistito nelle pratiche. Aggiungi, modifica o elimina le voci. Sono disponibili per tutte le pratiche dello studio.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 mr-1 cursor-pointer" title="Se attivo, converte in maiuscolo durante la digitazione">
              <Switch checked={ruoliUpper} onCheckedChange={setRuoliUpper} data-testid="ruoli-upper-toggle" className="data-[state=checked]:bg-amber-500" />
              <span className="text-xs text-slate-400">Solo maiuscolo</span>
            </label>
            <Button type="button" variant="outline" onClick={sortRuoliAZ} data-testid="ruoli-sort-az" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowDownAZ className="h-4 w-4 mr-1" /> Ordina A-Z
            </Button>
            <Button type="button" onClick={saveRuoli} disabled={ruoliSaving} data-testid="ruoli-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {ruoliSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
            </Button>
          </div>
        </div>
        <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-1">
          {ruoli.map((s, i) => (
            <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={() => onRuoloDrop(i)}
              className="flex items-center gap-2" data-testid={`ruolo-item-${i}`}>
              <span draggable onDragStart={() => setDragRuolo(i)} onDragEnd={() => setDragRuolo(null)} title="Trascina per riordinare"
                data-testid={`ruolo-drag-${i}`} className="shrink-0 cursor-move select-none text-slate-500 hover:text-slate-300 px-1 text-lg leading-none">⠿</span>
              <Input value={s} noUppercase onChange={(e) => setRuolo(i, ruoliUpper ? e.target.value.toUpperCase() : e.target.value)} data-testid={`ruolo-input-${i}`} className="h-9 bg-slate-900/60 border-slate-700" />
              <button type="button" onClick={() => delRuolo(i)} data-testid={`ruolo-del-${i}`} title="Elimina" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" onClick={addRuolo} data-testid="ruolo-add" className="mt-3 border-slate-700 text-slate-200 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Aggiungi voce</Button>
      </div>

      <div id="sez-tipologie-sinistro" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="tipologie-sinistro-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Settings className="h-4 w-4 text-amber-400" /> Tipologie Sinistro</h3>
            <p className="text-sm text-slate-400 mt-1">Tipologie di pratica/sinistro (codice e descrizione) usate nel wizard nuova pratica, nei filtri di Gestione Pratiche e nei modelli lettera. Aggiungi, modifica, elimina e riordina le voci.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={sortTipAZ} data-testid="tip-sort-az" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowDownAZ className="h-4 w-4 mr-1" /> Ordina A-Z
            </Button>
            <Button type="button" onClick={saveTip} disabled={tipSaving} data-testid="tip-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {tipSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
            </Button>
          </div>
        </div>
        <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-1">
          {tipologie.map((t, i) => (
            <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={() => onTipDrop(i)}
              className="rounded-md border border-slate-800 p-1.5" data-testid={`tip-item-${i}`}>
              <div className="flex items-center gap-2">
              <span draggable onDragStart={() => setDragTip(i)} onDragEnd={() => setDragTip(null)} title="Trascina per riordinare"
                data-testid={`tip-drag-${i}`} className="shrink-0 cursor-move select-none text-slate-500 hover:text-slate-300 px-1 text-lg leading-none">⠿</span>
              <Input value={t.code} onChange={(e) => setTipCode(i, e.target.value)} data-testid={`tip-code-${i}`} placeholder="Codice" className="h-9 w-40 shrink-0 bg-slate-900/60 border-slate-700 font-mono" />
              <Input value={t.label} noUppercase onChange={(e) => setTipLabel(i, e.target.value)} data-testid={`tip-label-${i}`} placeholder="Descrizione" className="h-9 bg-slate-900/60 border-slate-700" />
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(t.color || "") ? t.color : "#f59e0b"} onChange={(e) => setTipColor(i, e.target.value)} data-testid={`tip-color-${i}`} title="Colore badge" className="shrink-0 h-9 w-11 rounded-md border border-slate-700 bg-slate-900/60 cursor-pointer p-0.5" />
              <button type="button" onClick={() => delTip(i)} data-testid={`tip-del-${i}`} title="Elimina" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pl-7 pb-1">
                <label className={`flex items-center gap-2 text-xs ${t.solo_prof ? "text-slate-500" : "text-slate-300"} cursor-pointer`}>
                  <Switch checked={!t.solo_prof && t.veicolo !== false} disabled={!!t.solo_prof} onCheckedChange={(v) => setTipologie((l) => l.map((x, j) => (j === i ? { ...x, veicolo: !!v } : x)))} data-testid={`tip-veicolo-${i}`} />
                  Mostra dati veicolo
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <Switch checked={!!t.solo_prof} onCheckedChange={(v) => setTipologie((l) => l.map((x, j) => (j === i ? { ...x, solo_prof: !!v } : x)))} data-testid={`tip-solo-prof-${i}`} />
                  Tipo senza veicolo (solo professionisti)
                </label>
              </div>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" onClick={addTip} data-testid="tip-add" className="mt-3 border-slate-700 text-slate-200 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Aggiungi tipologia</Button>
      </div>

      <div id="sez-campi-copertina" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="campi-copertina-config">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><Settings className="h-4 w-4 text-amber-400" /> Campi Copertina Pratica</h3>
            <p className="text-sm text-slate-400 mt-1">Attiva/disattiva i campi e trascinali per ordinarli. Crea più modelli (es. Sinistro RCA, Infortuni…) e scegli quale usare alla stampa.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={() => setCopTplOpen(true)} data-testid="cop-edit-template" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"><FileText className="h-4 w-4 mr-1" /> Modifica Template</Button>
            <Button type="button" onClick={saveCop} disabled={copSaving} data-testid="cop-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {copSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva
            </Button>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2.5">
          <span className="text-xs text-slate-400">Modello:</span>
          <select value={copModelId} onChange={(e) => selectCopModel(e.target.value)} data-testid="cop-model-select"
            className="h-8 rounded border border-slate-700 bg-slate-900/60 text-sm text-slate-100 px-2 min-w-[180px]">
            <option value="__default__" className="bg-slate-900 text-slate-100">Predefinito</option>
            {copModels.map((m) => <option key={m.id} value={m.id} className="bg-slate-900 text-slate-100">{m.nome}</option>)}
          </select>
          <Button type="button" variant="outline" size="sm" onClick={newCopModel} data-testid="cop-model-new" className="border-slate-700 text-slate-200 hover:bg-slate-800"><Plus className="h-3.5 w-3.5 mr-1" /> Nuovo</Button>
          <Button type="button" variant="outline" size="sm" onClick={renameCopModel} disabled={copModelId === "__default__"} data-testid="cop-model-rename" className="border-slate-700 text-slate-200 hover:bg-slate-800"><Pencil className="h-3.5 w-3.5 mr-1" /> Rinomina</Button>
          <Button type="button" variant="outline" size="sm" onClick={deleteCopModel} disabled={copModelId === "__default__"} data-testid="cop-model-delete" className="border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina</Button>
        </div>
        <Dialog open={copTplOpen} onOpenChange={setCopTplOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto bg-[#0f141e] border-slate-700" data-testid="cop-template-dialog">
            <DialogHeader><DialogTitle className="text-slate-100">Modifica Template — {copModelId === "__default__" ? "Predefinito" : (copModels.find((m) => m.id === copModelId)?.nome || "")}</DialogTitle></DialogHeader>
            <CopertinaEditor key={copModelId} templateValue={copTemplate} onChange={setCopTemplate} onSaveTemplate={saveCopTemplate} />
          </DialogContent>
        </Dialog>
        <div className="mt-4 space-y-2">
          {copFields.map((f, i) => {
            const st = copStyles[f.key] || {};
            return (
            <div key={f.key} draggable onDragStart={() => setDragCop(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onCopDrop(i)} data-testid={`cop-item-${f.key}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2.5 cursor-move hover:bg-slate-800/40 flex-wrap">
              <span className="flex items-center gap-2 text-sm text-slate-200 min-w-0"><span className="text-slate-500 font-mono select-none">⋮⋮</span> <span className="truncate">{f.label}</span></span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" draggable={false} onClick={() => setCopStyle(f.key, { upper: !st.upper })} data-testid={`cop-upper-${f.key}`} title="Maiuscolo (on/off)"
                  className={`h-7 w-7 rounded border text-xs font-bold transition-colors ${st.upper ? "bg-amber-500 border-amber-500 text-[#0a0d14]" : "border-slate-700 text-slate-300 hover:text-amber-400"}`}>AA</button>
                <button type="button" draggable={false} onClick={() => setCopStyle(f.key, { bold: !st.bold })} data-testid={`cop-bold-${f.key}`} title="Grassetto (on/off)"
                  className={`h-7 w-7 rounded border text-xs font-bold transition-colors ${st.bold ? "bg-amber-500 border-amber-500 text-[#0a0d14]" : "border-slate-700 text-slate-300 hover:text-amber-400"}`}>G</button>
                <select value={st.size || 10} draggable={false} onChange={(e) => setCopStyle(f.key, { size: Number(e.target.value) })} data-testid={`cop-size-${f.key}`} title="Dimensione font"
                  className="h-7 rounded border border-slate-700 bg-slate-900/60 text-xs text-slate-100 px-1">
                  {COP_SIZE_OPTS.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-100">{s}pt</option>)}
                </select>
                <select value={st.highlight || ""} draggable={false} onChange={(e) => setCopStyle(f.key, { highlight: e.target.value })} data-testid={`cop-highlight-${f.key}`} title="Colore evidenziazione"
                  className="h-7 rounded border border-slate-700 text-xs px-1" style={{ backgroundColor: st.highlight || "#0f172a", color: st.highlight ? "#1a1a1a" : "#e2e8f0" }}>
                  {COP_HIGHLIGHT_OPTS.map((h) => <option key={h.v} value={h.v} style={{ backgroundColor: h.v || "#0f172a", color: h.v ? "#1a1a1a" : "#e2e8f0" }}>{h.label}</option>)}
                </select>
                <Switch checked={f.enabled} onCheckedChange={() => toggleCop(f.key)} data-testid={`cop-switch-${f.key}`} />
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <WaTemplatesSettings initial={form.wa_templates} onSaved={(v) => setForm((f) => ({ ...f, wa_templates: v }))} />

      <FrasarioDiarioSettings />

      <div id="sez-tariffe" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="tariffe-config">
        <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><BadgeEuro className="h-4 w-4 text-amber-400" /> Tariffe e Onorari</h3>
        <p className="text-sm text-slate-400 mt-1">Configurazione delle tariffe e degli onorari dello studio.</p>
        <p className="mt-4 text-sm text-slate-500 border border-dashed border-slate-700 rounded-lg px-4 py-6 text-center">Sezione in arrivo — la gestione di tariffe e onorari sarà disponibile a breve.</p>
      </div>

      <div id="sez-fatturazione" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="fatturazione-config">
        <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><ReceiptText className="h-4 w-4 text-amber-400" /> Impostazioni Fatturazione</h3>
        <p className="text-sm text-slate-400 mt-1">Impostazioni predefinite applicate alle nuove fatture proforma: regime fiscale, aliquote e codice SDI. Le aliquote si applicano automaticamente ai regimi personalizzabili (Ordinario, Altro).</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300 text-xs">Regime fiscale di default</Label>
            <select value={fatt.regime_default || "forfettario"} onChange={(e) => setFattField("regime_default", e.target.value)} data-testid="fatt-regime" className="mt-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
              <option value="forfettario" className="bg-slate-900">Regime Forfettario</option>
              <option value="ordinario" className="bg-slate-900">Regime Ordinario</option>
              <option value="minimi" className="bg-slate-900">Regime dei Minimi</option>
              <option value="altro" className="bg-slate-900">Altro regime</option>
            </select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Codice SDI</Label>
            <Input value={fatt.codice_sdi || ""} onChange={(e) => setFattField("codice_sdi", e.target.value)} placeholder="Es. 0000000" data-testid="fatt-sdi" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Aliquota IVA %</Label>
            <Input type="number" step="1" value={fatt.iva_pct ?? ""} onChange={(e) => setFattField("iva_pct", e.target.value)} placeholder="22" data-testid="fatt-iva" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Ritenuta d'acconto %</Label>
            <Input type="number" step="1" value={fatt.ritenuta_pct ?? ""} onChange={(e) => setFattField("ritenuta_pct", e.target.value)} placeholder="20" data-testid="fatt-ritenuta" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Cassa previdenziale %</Label>
            <Input type="number" step="0.5" value={fatt.cassa_pct ?? ""} onChange={(e) => setFattField("cassa_pct", e.target.value)} placeholder="4" data-testid="fatt-cassa" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
          </div>
        </div>
        <label className="mt-4 flex items-center gap-2 cursor-pointer" data-testid="fatt-mostra-filiale-row">
          <Checkbox checked={!!fatt.mostra_filiale} onCheckedChange={(v) => setFattField("mostra_filiale", !!v)} data-testid="fatt-mostra-filiale" className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
          <span className="text-sm text-slate-200">Mostra filiale banca nelle stampe</span>
        </label>
        <Button type="button" onClick={saveFatt} disabled={savingFatt} data-testid="fatt-save" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {savingFatt ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva impostazioni
        </Button>
      </div>

      <div id="sez-impostazioni-fattura" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="impostazioni-fattura-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" /> Diciture Legali</h3>
            <p className="text-sm text-slate-400 mt-1">Crea, modifica ed elimina i modelli di dicitura legale per regime fiscale. Il modello "predefinito" di un regime viene applicato automaticamente alla proforma quando si seleziona quel regime; sulla singola fattura è possibile scegliere un modello alternativo. Il regime Forfettario ha già una dicitura precaricata.</p>
          </div>
          <Button type="button" onClick={addDic} data-testid="dic-add" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
        </div>
        <div className="mt-4 space-y-3">
          {diciture.length === 0 && <p className="text-sm text-slate-500">Nessun modello di dicitura.</p>}
          {diciture.map((d, i) => (
            <div key={d.id || i} className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid={`dic-row-${i}`}>
              <div className="grid sm:grid-cols-[1fr_170px_auto] gap-2 items-center">
                <Input value={d.nome || ""} onChange={(e) => setDic(i, "nome", e.target.value)} placeholder="Nome modello" data-testid={`dic-nome-${i}`} className="h-9 bg-slate-900/60 border-slate-700" />
                <select value={d.regime || "forfettario"} onChange={(e) => setDic(i, "regime", e.target.value)} data-testid={`dic-regime-${i}`} className="h-9 rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
                  <option value="forfettario" className="bg-slate-900">Forfettario</option>
                  <option value="ordinario" className="bg-slate-900">Ordinario</option>
                  <option value="minimi" className="bg-slate-900">Minimi</option>
                  <option value="altro" className="bg-slate-900">Altro</option>
                </select>
                <button type="button" onClick={() => delDic(i)} data-testid={`dic-del-${i}`} className="text-red-400 hover:text-red-300 justify-self-end"><Trash2 className="h-4 w-4" /></button>
              </div>
              <textarea value={d.testo || ""} onChange={(e) => setDic(i, "testo", e.target.value)} rows={2} placeholder="Testo della dicitura legale" data-testid={`dic-testo-${i}`} className="w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100" />
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={!!d.principale} onChange={(e) => setPrincipale(i, e.target.checked)} data-testid={`dic-princ-${i}`} /> Modello predefinito per il regime selezionato
              </label>
            </div>
          ))}
        </div>
        <Button type="button" onClick={saveDic} disabled={savingDic} data-testid="dic-save" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {savingDic ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva modelli
        </Button>
      </div>

      <div id="sez-voci-proforma" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="voci-proforma-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" /> Voci Proforma Predefinite</h3>
            <p className="text-sm text-slate-400 mt-1">Voci riutilizzabili (descrizione, quantità, prezzo unitario) selezionabili al volo nella creazione delle fatture proforma.</p>
          </div>
          <Button type="button" onClick={addPV} data-testid="pv-add" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
        </div>
        <div className="mt-4 space-y-2">
          {proformaVoci.length === 0 && <p className="text-sm text-slate-500">Nessuna voce predefinita.</p>}
          {proformaVoci.map((v, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_110px_auto] gap-2 items-center" data-testid={`pv-row-${i}`}>
              <Input value={v.descrizione || ""} onChange={(e) => setPV(i, "descrizione", e.target.value)} placeholder="Descrizione" data-testid={`pv-desc-${i}`} className="h-9 bg-slate-900/60 border-slate-700" />
              <Input type="number" step="1" value={v.quantita ?? 1} onChange={(e) => setPV(i, "quantita", e.target.value)} placeholder="Q.tà" data-testid={`pv-qta-${i}`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
              <Input type="number" step="0.01" value={v.prezzo_unitario ?? 0} onChange={(e) => setPV(i, "prezzo_unitario", e.target.value)} placeholder="Prezzo €" data-testid={`pv-unit-${i}`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
              <button type="button" onClick={() => delPV(i)} data-testid={`pv-del-${i}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button type="button" onClick={savePV} disabled={savingPV} data-testid="pv-save" className="mt-4 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {savingPV ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva voci
        </Button>
      </div>

      <div id="sez-abbonamento" className="pgp-card p-6 max-w-2xl mt-6 pgp-fade-up" data-testid="abbonamento-config">
        <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-400" /> Abbonamento</h3>
        <p className="text-sm text-slate-400 mt-1">Dati del tuo abbonamento (sola lettura).</p>
        <AbbonamentoCard />
      </div>

      <AreaClienteHeaderSettings />
    </div>
  );
}
